import { defineStore } from 'pinia'
import rbacService from '@/api/services/rbac'

/**
 * Permissions Store
 *
 * Manages user permissions loaded from the JWT/backend.
 *
 * ROLES:
 * - Super Admin: Highest privilege level, has all permissions
 * - Admin: Standard admin, no longer has dangerous operations
 * - Manager, User, etc: Lower privilege levels
 *
 * SUPER ADMIN PERMISSIONS (dangerous operations):
 * - company:rename     - Rename company across all tables
 * - company:purge      - Delete company and all associated data
 * - system:admin       - Access system-level admin settings
 * - system:migrations  - Run database migrations
 * - data:export_all    - Export all system data
 * - data:import        - Import data into system
 * - users:impersonate  - Impersonate other users
 * - users:delete       - Delete user accounts
 * - portfolio:purge    - Delete portfolios and associated data
 * - audit:read         - View audit logs
 * - audit:export       - Export audit logs
 */
export const usePermissionsStore = defineStore('permissions', {
  state: () => ({
    // User's assigned roles
    roles: [],
    // Flat array of permission strings (e.g., ['prompts:read', 'prompts:write'])
    permissions: [],
    // User info from RBAC endpoint
    userId: null,
    username: null,
    email: null,
    // Loading states
    isLoading: false,
    // Error state
    error: null,
    // Last loaded timestamp
    lastLoaded: null,
  }),

  getters: {
    /**
     * Check if user has a specific permission
     * @param {string} permission - Permission string (e.g., 'prompts:read')
     * @returns {boolean}
     */
    hasPermission: (state) => (permission) => {
      return state.permissions.includes(permission)
    },

    /**
     * Check if user has any of the specified permissions (OR logic)
     * @param {Array<string>} permissions - Array of permission strings
     * @returns {boolean}
     */
    hasAnyPermission: (state) => (permissions) => {
      return permissions.some(p => state.permissions.includes(p))
    },

    /**
     * Check if user has all of the specified permissions (AND logic)
     * @param {Array<string>} permissions - Array of permission strings
     * @returns {boolean}
     */
    hasAllPermissions: (state) => (permissions) => {
      return permissions.every(p => state.permissions.includes(p))
    },

    /**
     * Check if user has a specific role
     * @param {string} roleName - Role name to check
     * @returns {boolean}
     */
    hasRole: (state) => (roleName) => {
      return state.roles.includes(roleName)
    },

    /**
     * Check if user has any of the specified roles
     * @param {Array<string>} roleNames - Array of role names
     * @returns {boolean}
     */
    hasAnyRole: (state) => (roleNames) => {
      return roleNames.some(r => state.roles.includes(r))
    },

    /**
     * Check if permissions have been loaded
     */
    isLoaded: (state) => {
      return state.lastLoaded !== null
    },

    /**
     * Check if data needs refresh (older than 5 minutes)
     */
    needsRefresh: (state) => {
      if (!state.lastLoaded) return true
      const fiveMinutesAgo = Date.now() - (5 * 60 * 1000)
      return state.lastLoaded < fiveMinutesAgo
    }
  },

  actions: {
    /**
     * Load user permissions from the backend
     * @param {boolean} force - Force reload even if recently loaded
     */
    async loadPermissions(force = false) {
      // Skip if recently loaded and not forcing
      if (!force && !this.needsRefresh && this.isLoaded) {
        return { success: true, fromCache: true }
      }

      this.isLoading = true
      this.error = null

      try {
        const response = await rbacService.getMyPermissions()

        this.userId = response.userId
        this.username = response.username
        this.email = response.email
        this.roles = response.roles || []
        this.permissions = response.permissions || []
        this.lastLoaded = Date.now()

        return { success: true, permissions: this.permissions, roles: this.roles }
      } catch (error) {
        console.error('Error loading permissions:', error)
        this.error = error.response?.data?.detail || error?.data?.detail || 'Failed to load permissions'
        return { success: false, error: this.error }
      } finally {
        this.isLoading = false
      }
    },

    /**
     * Set permissions directly (useful for SSR or testing)
     * @param {Array<string>} permissions - Array of permission strings
     * @param {Array<string>} roles - Array of role names
     */
    setPermissions(permissions, roles) {
      this.permissions = permissions || []
      this.roles = roles || []
      this.lastLoaded = Date.now()
      this.error = null
    },

    /**
     * Clear all permissions and reset state (call on logout)
     */
    clearPermissions() {
      this.roles = []
      this.permissions = []
      this.userId = null
      this.username = null
      this.email = null
      this.error = null
      this.lastLoaded = null
    }
  }
})
